import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {Trans} from '@common/i18n/trans';
import {useTrans} from '@common/i18n/use-trans';
import {message} from '@common/i18n/message';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {apiClient} from '@common/http/query-client';
import {BLANK_PAGE_SKELETON} from '@app/projects/blank-page-skeleton';
import {TemplateGrid} from './TemplateGrid';
import {useAiProjectBuilder, AiDebugInfo} from '@app/projects/use-ai-project-builder';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import {AxiosError} from 'axios';
import styles from './NewProjectPage.module.css';
import {DashboardNavbar} from './dashboard-navbar';
import {DashboardBottomNav} from './dashboard-sidebar';

type BuilderTemplate = {
  id: string;
  name: string;
  thumbnail: string;
  updated_at?: string;
  config?: {
    category?: string;
    version?: string;
    [key: string]: any;
  };
};

type PaginationResponse<T> = {
  pagination: {
    data: T[];
  };
};

type CreationMode = 'ai' | 'template' | 'blank';

const GEN_STEPS = [
  'Analyzing your idea',
  'Planning site structure',
  'Generating sections',
  'Applying styles & colors',
  'Finalizing your website',
];

const NP_CATEGORIES: string[] = [
  'Landing Page', 'Portfolio', 'E-commerce', 'Blog', 'Restaurant',
  'Agency', 'SaaS', 'Event', 'Personal Brand', 'Dashboard',
];

const NP_PROMPTS: Record<string, string[]> = {
  _default: [
    'A modern SaaS landing page with pricing, testimonials, and FAQ.',
    'A personal portfolio with projects, about section, and contact form.',
    'An e-commerce storefront with product grid and cart flow.',
  ],
  'Landing Page': [
    'A SaaS product launch page with animated hero and pricing table.',
    'A startup landing page with feature comparison and CTA.',
    'An app download page with screenshots and reviews.',
  ],
  'Portfolio': [
    'A creative developer portfolio with dark mode and project cards.',
    'A photographer showcase with full-bleed gallery and booking.',
    'A UX designer portfolio with case studies and testimonials.',
  ],
  'E-commerce': [
    'A fashion store with product grid, filters, and cart drawer.',
    'A boutique shop with collection pages and lookbooks.',
    'A digital product shop with download and preview sections.',
  ],
  'Blog': [
    'A minimal tech blog with dark mode and code highlighting.',
    'A newsletter landing page with archive and subscribe form.',
    'A personal journal with categories and full-text search.',
  ],
  'Restaurant': [
    'A fine dining restaurant with menu, gallery, and reservations.',
    'A cafe website with seasonal menu and online ordering CTA.',
    'A food truck site with location map and social links.',
  ],
  'Agency': [
    'A creative agency homepage with animated hero and case studies.',
    'A digital marketing agency with services and team section.',
    'A design studio with portfolio grid and process steps.',
  ],
  'SaaS': [
    'A B2B SaaS tool with feature grid, pricing table, and FAQ.',
    'An AI platform page with demo section and integrations.',
    'A project management tool with screenshots and testimonials.',
  ],
  'Event': [
    'A tech conference page with speakers, agenda, and tickets.',
    'A music festival site with lineup, schedule, and maps.',
    'A webinar landing with countdown and registration form.',
  ],
  'Personal Brand': [
    'A speaker and author page with bio, talks, and booking.',
    'A coach website with services, testimonials, and calendar.',
    'A personal blog with newsletter and social links.',
  ],
  'Dashboard': [
    'A SaaS analytics dashboard with charts, cards, and sidebar.',
    'A finance tracker with budget widgets and transaction list.',
    'An admin panel with user management and activity feed.',
  ],
};

const AI_PROMPT_MIN_LENGTH = 12;
const AI_PROMPT_MAX_LENGTH = 800;

const STORAGE_KEY = 'be.newProjectState';

export function NewProjectPage() {
  const {trans} = useTrans();
  const navigate = useNavigate();
  const {isSubscribed, user, hasPermission} = useAuth();
  const aiBuilder = useAiProjectBuilder();
  const settings = useSettings() as any;

  // Read admin feature flags — default to enabled if not explicitly set
  const aiModeEnabled      = settings?.client?.builder?.ai_mode      !== false;
  const templateModeEnabled = settings?.client?.builder?.template_mode !== false;
  const dragDropModeEnabled = settings?.client?.builder?.drag_drop_mode !== false;

  // Ordered list of modes that admin has enabled
  const enabledModes = useMemo((): CreationMode[] => {
    const m: CreationMode[] = [];
    if (aiModeEnabled)      m.push('ai');
    if (templateModeEnabled) m.push('template');
    if (dragDropModeEnabled) m.push('blank');
    return m;
  }, [aiModeEnabled, templateModeEnabled, dragDropModeEnabled]);

  const defaultMode: CreationMode = enabledModes[0] ?? 'template';

  const [mode, setMode] = useState<CreationMode>(defaultMode);
  const [projectName, setProjectName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | 'blank' | null>(null);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>('all');
  const [templateSearch, setTemplateSearch] = useState('');

  const [allTemplates, setAllTemplates] = useState<BuilderTemplate[]>([]);
  const [templates, setTemplates] = useState<BuilderTemplate[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiPrompt, setAiPrompt] = useState('');
  const [activeAiCategory, setActiveAiCategory] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [quotaMessage, setQuotaMessage] = useState<string | null>(null);

  // Multi-step generation progress
  const [genStep, setGenStep] = useState(0);
  const genTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Admin debug state
  const isAdmin = hasPermission('admin');
  const [debugInfo, setDebugInfo] = useState<AiDebugInfo | null>(null);
  const [debugRequest, setDebugRequest] = useState<Record<string, string> | null>(null);
  const [genLog, setGenLog] = useState<Array<{ts: number; msg: string; type: 'info'|'ok'|'err'}>>([]);

  const canCreateProject = !!projectName.trim() && !quotaBlocked;

  const isRestoringState = useRef(true);
  const aiTextareaRef = useRef<HTMLTextAreaElement>(null);
  const tplChipsRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const el = aiTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [aiPrompt]);

  const resetAi = useCallback(() => {
    setAiPrompt('');
    setAiError(null);
    setActiveAiCategory(null);
  }, []);

  const handleModeSelect = useCallback(
    (nextMode: CreationMode) => {
      if (nextMode === mode) return;
    setError(null);
    setProjectName('');
    setQuotaBlocked(false);
    setQuotaMessage(null);

    // Block mode if admin has disabled it
    const modeAllowedByAdmin =
      (nextMode === 'ai'       && aiModeEnabled) ||
      (nextMode === 'template' && templateModeEnabled) ||
      (nextMode === 'blank'    && dragDropModeEnabled);
    if (!modeAllowedByAdmin) return;

    if (nextMode === 'ai' && !isSubscribed) {
      setError(trans(message('AI builder is available on paid plans. Upgrade to use it.')));
      return;
    }

    if (nextMode === 'ai') {
      resetAi();
      setSelectedTemplate(null);
    } else if (nextMode === 'template') {
      setSelectedTemplateCategory('all');
      setSelectedTemplate(null);
      setTemplateSearch('');
    } else {
      setSelectedTemplate('blank');
      setSelectedTemplateCategory('all');
    }

      setMode(nextMode);
    },
    [mode, resetAi, aiModeEnabled, templateModeEnabled, dragDropModeEnabled, isSubscribed, trans],
  );

  const uniqueCategories = useMemo(() => {
    const set = new Set<string>();
    allTemplates.forEach(t => {
      const cat = (t.config?.category || '').trim();
      if (cat) set.add(cat);
    });
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [allTemplates]);

  const fetchAllTemplatesForCategories = useCallback(async () => {
    setLoadingCategories(true);
    setError(null);
    try {
      const res = await apiClient.get<PaginationResponse<BuilderTemplate>>('templates', {
        params: {perPage: 200, orderBy: 'updated_at', orderDir: 'desc'},
      });
      setAllTemplates(res.data.pagination.data || []);
    } catch (_err) {
      setError('Could not load templates.');
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchTemplatesByCategory = useCallback(
    async (category: string) => {
      setLoadingTemplates(true);
      setError(null);
      try {
        const params: Record<string, any> = {perPage: 24, orderBy: 'updated_at', orderDir: 'desc'};
        if (category !== 'all') params.category = category;
        const res = await apiClient.get<PaginationResponse<BuilderTemplate>>('templates', {params});
        setTemplates(res.data.pagination.data || []);
      } catch (_err) {
        setError('Could not load templates.');
      } finally {
        setLoadingTemplates(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchAllTemplatesForCategories();
  }, [fetchAllTemplatesForCategories]);

  useEffect(() => {
    if (mode === 'template') {
      fetchTemplatesByCategory(selectedTemplateCategory);
    }
  }, [mode, selectedTemplateCategory, fetchTemplatesByCategory]);

  useEffect(() => {
    if (!isRestoringState.current) return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      isRestoringState.current = false;
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      if (parsed.mode && ['ai', 'template', 'blank'].includes(parsed.mode)) {
        const requestedMode = parsed.mode as CreationMode;
        const modeAllowedByAdmin =
          (requestedMode === 'ai'       && aiModeEnabled) ||
          (requestedMode === 'template' && templateModeEnabled) ||
          (requestedMode === 'blank'    && dragDropModeEnabled);
        if (!modeAllowedByAdmin) {
          setMode(defaultMode);
        } else if (requestedMode === 'ai' && !isSubscribed) {
          setMode(enabledModes.find(m => m !== 'ai') ?? defaultMode);
        } else {
          setMode(requestedMode);
        }
      }
      if (typeof parsed.projectName === 'string') {
        setProjectName(parsed.projectName);
      }
      if (typeof parsed.selectedTemplate === 'string') {
        setSelectedTemplate(parsed.selectedTemplate);
      }
      if (typeof parsed.selectedTemplateCategory === 'string') {
        setSelectedTemplateCategory(parsed.selectedTemplateCategory);
      }
      if (typeof parsed.templateSearch === 'string') {
        setTemplateSearch(parsed.templateSearch);
      }
      if (typeof parsed.aiPrompt === 'string') {
        setAiPrompt(parsed.aiPrompt);
      }
    } catch (_err) {
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      isRestoringState.current = false;
    }
  }, [isSubscribed]);

  useEffect(() => {
    if (isRestoringState.current) return;
    const state = {
      mode,
      selectedTemplateCategory,
      selectedTemplate,
      templateSearch,
      projectName,
      aiPrompt,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_err) {}
  }, [
    mode,
    selectedTemplateCategory,
    selectedTemplate,
    templateSearch,
    projectName,
    aiPrompt,
  ]);

  const handleTemplateSelection = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    const resolvedTemplate = mode === 'template' ? (selectedTemplate ?? 'blank') : selectedTemplate;

    const slug = projectName
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');

    const payload = {
      name: projectName.trim(),
      pages: resolvedTemplate === 'blank' ? [{name: 'index', html: BLANK_PAGE_SKELETON}] : [],
      templateName: resolvedTemplate === 'blank' ? undefined : resolvedTemplate,
      slug,
    };

    try {
      const response = await apiClient.post('projects', payload);
      navigate(`/editor/${response.data.project.model.id}`);
    } catch (err) {
      const axiosErr = err as AxiosError<any>;
      if (axiosErr.response?.status === 403) {
        const apiMessage = axiosErr.response?.data?.message;
        setQuotaBlocked(true);
        setQuotaMessage(apiMessage || trans(message('You reached the project limit for this plan. Upgrade to continue.')));
        setError(null);
      } else {
        setError('Could not create project. Try again.');
      }
      console.error(err);
    }
  };

  const startGenSteps = useCallback(() => {
    setGenStep(0);
    let step = 0;
    const intervals = [1200, 2400, 3600, 5200]; // ms per step (step 4 waits for API)
    let i = 0;
    const advance = () => {
      if (i < intervals.length) {
        genTimerRef.current = setTimeout(() => {
          step += 1;
          setGenStep(step);
          i += 1;
          advance();
        }, intervals[i]);
      }
    };
    advance();
  }, []);

  const stopGenSteps = useCallback(() => {
    if (genTimerRef.current) clearTimeout(genTimerRef.current);
    setGenStep(GEN_STEPS.length - 1);
  }, []);

  const addLog = useCallback((msg: string, type: 'info'|'ok'|'err' = 'info') => {
    setGenLog(prev => [...prev, {ts: Date.now(), msg, type}]);
  }, []);

  const handleGenerateWithAi = () => {
    const prompt = aiPrompt.trim();
    if (!prompt) {
      setAiError('Describe the website you want to build.');
      return;
    }
    if (prompt.length < AI_PROMPT_MIN_LENGTH) {
      setAiError(`Prompt must be at least ${AI_PROMPT_MIN_LENGTH} characters.`);
      return;
    }
    if (prompt.length > AI_PROMPT_MAX_LENGTH) {
      setAiError(`Prompt must be ${AI_PROMPT_MAX_LENGTH} characters or fewer.`);
      return;
    }

    const payload = {
      prompt,
      brand: 'New website',
      audience: 'General audience',
      tone: 'Balanced and modern',
    };

    setAiError(null);
    setDebugInfo(null);
    if (isAdmin) {
      setDebugRequest(payload);
      setGenLog([]);
      addLog(`POST /api/projects/ai  [prompt: ${prompt.length} chars]`);
    }
    startGenSteps();

    aiBuilder.mutate(payload, {
      onSuccess: data => {
        stopGenSteps();
        if (isAdmin && data.debug) {
          setDebugInfo(data.debug);
          addLog(`✓ Success — ${data.debug.tokens ?? '?'} tokens, ${data.debug.elapsed_ms ?? '?'}ms`, 'ok');
        }
        resetAi();
        sessionStorage.removeItem(STORAGE_KEY);
        navigate(`/editor/${data.project.model.id}`);
      },
      onError: (err: AxiosError<any>) => {
        stopGenSteps();

        // responseData may be null/HTML when PHP crashes with a fatal (timeout, OOM, etc.)
        const rawData = err.response?.data;
        const isJsonBody = rawData && typeof rawData === 'object';
        const responseData: Record<string, any> = isJsonBody ? rawData : {};
        const statusCode = err.response?.status ?? 0;
        const isTimeout = statusCode === 500 && !isJsonBody;

        if (isAdmin) {
          const dbg: AiDebugInfo = {...(responseData.debug ?? {})};
          dbg.status = 'error';
          if (isTimeout) {
            dbg.exception = 'FatalError (PHP timeout / OOM)';
            dbg.message   = 'PHP max_execution_time exceeded or out-of-memory. The server killed the process before the AI response arrived. Check php.ini max_execution_time (should be ≥ 180).';
          }
          setDebugInfo(dbg);
          addLog(`✗ HTTP ${statusCode} — ${dbg.exception ?? err.message}`, 'err');
          if (isTimeout) {
            addLog('Hint: set_time_limit(180) is in the controller. If still timing out, check php.ini / php-fpm pool settings.', 'err');
          }
        }

        const validationErrors = responseData.errors;
        if (validationErrors && typeof validationErrors === 'object') {
          const firstKey = Object.keys(validationErrors)[0];
          const firstMessage = firstKey ? validationErrors[firstKey]?.[0] : null;
          if (firstMessage && typeof firstMessage === 'string') {
            setAiError(firstMessage);
            return;
          }
        }
        if (isTimeout) {
          setAiError('The server timed out while generating your project. This usually means the AI took too long to respond. Try a simpler prompt or check server settings.');
          return;
        }
        setAiError(responseData.message || 'Generation failed. Try again.');
      },
    });
  };

  const aiSubmitDisabled = !isSubscribed || aiBuilder.isPending || !aiPrompt.trim();

  const filteredTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter(template => {
      const name = template.name?.toLowerCase() ?? '';
      const category = template.config?.category?.toLowerCase() ?? '';
      return name.includes(query) || category.includes(query);
    });
  }, [templateSearch, templates]);

  const renderTemplatePanel = () => (
    <div className={styles.templatePanel}>

      {/* Header */}
      <div className={styles.tplHeader}>
        <div className={styles.tplHeaderLeft}>
          <span className={styles.tplTitle}><Trans message="Choose a template" /></span>
          <span className={styles.tplSubtitle}><Trans message="Pick a starting point and make it yours." /></span>
        </div>
        <div className={styles.tplSearchWrap}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={styles.tplSearchIcon}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className={styles.tplSearchInput}
            placeholder={trans(message('Search templates...'))}
            value={templateSearch}
            onChange={e => setTemplateSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Category chips row */}
      {loadingCategories ? (
        <span className={styles.loadingText}><Trans message="Loading categories…" /></span>
      ) : (
        <div className={styles.tplChipsRow}>
          <button
            type="button"
            className={styles.tplChipsNav}
            aria-label="Scroll left"
            onClick={() => tplChipsRef.current?.scrollBy({left: -200, behavior: 'smooth'})}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div className={styles.tplChipsList} ref={tplChipsRef}>
            {uniqueCategories.map(category => (
              <button
                key={category}
                type="button"
                className={clsx(styles.tplChip, selectedTemplateCategory === category && styles.tplChipActive)}
                onClick={() => { setSelectedTemplateCategory(category); setSelectedTemplate(null); }}
              >
                {category === 'all' ? trans(message('All')) : category}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.tplChipsNav}
            aria-label="Scroll right"
            onClick={() => tplChipsRef.current?.scrollBy({left: 200, behavior: 'smooth'})}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
      )}

      {/* Template grid */}
      <div className={styles.templateGridWrap}>
        <AnimatePresence mode="wait">
          {loadingTemplates ? (
            <motion.div key="tpl-loading" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className={styles.tplSkeletonGrid}>
                {Array.from({length: 6}).map((_, i) => <div key={i} className={styles.tplSkeleton} />)}
              </div>
            </motion.div>
          ) : filteredTemplates.length ? (
            <motion.div key={`tpl-${selectedTemplateCategory}`} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:.15,ease:'easeOut'}}>
              <TemplateGrid
                templates={filteredTemplates.map(t => ({id:t.id, name:t.name, thumbnail:t.thumbnail, category:t.config?.category}))}
                loading={false}
                onSelectTemplate={handleTemplateSelection}
                selectedTemplate={selectedTemplate}
                proceedToFinalStep={() => undefined}
                categories={[]}
              />
            </motion.div>
          ) : templates.length && templateSearch.trim() ? (
            <motion.div key="tpl-search-empty" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className={styles.placeholder}><Trans message="No templates match your search." /></div>
            </motion.div>
          ) : (
            <motion.div key="tpl-empty" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className={styles.placeholder}><Trans message="No templates in this category." /></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Create bar */}
      <div className={styles.tplCreateBar}>
        {selectedTemplate && (
          <span className={styles.tplSelectedHint}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
            <Trans message="Template selected" />
          </span>
        )}
        <div className={styles.nameRow}>
          <input
            type="text"
            className={styles.nameInput}
            placeholder={trans(message('Project name'))}
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canCreateProject && handleCreateProject()}
          />
          <button
            type="button"
            className={styles.createBtn}
            disabled={!canCreateProject}
            onClick={handleCreateProject}
          >
            <Trans message="Create project" />
          </button>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {quotaMessage && (
          <div className={styles.notice}>
            <span>{quotaMessage}</span>
            <a href="/pricing" className={styles.noticeLink}><Trans message="Upgrade →" /></a>
          </div>
        )}
      </div>
    </div>
  );

  const renderBlankPanel = () => (
    <div className={styles.blankPanel}>
      <div className={styles.crumb}>
        <div className={clsx(styles.crumbItem, projectName.trim() ? styles.crumbItemDone : styles.crumbItemActive)}>
          <span className={styles.crumbDot} />
          <span className={styles.crumbText}><Trans message="Name" /></span>
        </div>
        <span className={styles.crumbSep}>›</span>
        <div className={clsx(styles.crumbItem, projectName.trim() ? styles.crumbItemActive : '')}>
          <span className={styles.crumbDot} />
          <span className={styles.crumbText}><Trans message="Create" /></span>
        </div>
      </div>

      <div className={styles.nameSection}>
        <span className={styles.nameSectionLabel}><Trans message="Project name" /></span>
        <div className={styles.nameRow}>
          <input
            type="text"
            className={styles.nameInput}
            placeholder={trans(message('Project name'))}
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canCreateProject && handleCreateProject()}
          />
          <button
            type="button"
            className={styles.createBtn}
            disabled={!canCreateProject}
            onClick={handleCreateProject}
          >
            <Trans message="Create" />
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {quotaMessage && (
        <div className={styles.notice}>
          <span>{quotaMessage}</span>
          <a href="/pricing" className={styles.noticeLink}><Trans message="Upgrade →" /></a>
        </div>
      )}

      <div className={styles.blankPreviewBlock}>
        <span className={styles.blankPreviewLabel}><Trans message="Blank theme preview" /></span>
        <span className={styles.blankPreviewHint}><Trans message="Start clean and style every section yourself." /></span>
        <div className={styles.blankPreview} aria-hidden="true">
          <div className={styles.blankBar}>
            <span className={styles.blankDot} />
            <span className={styles.blankTopLine} />
            <span className={styles.blankTopLineShort} />
          </div>
          <div className={styles.blankHero} />
          <div className={styles.blankGrid}>
            <span className={styles.blankCard} />
            <span className={styles.blankCard} />
            <span className={styles.blankCard} />
          </div>
          <div className={styles.blankFooter} />
        </div>
      </div>
    </div>
  );

  const renderAiPanel = () => {
    const prompts =
      NP_PROMPTS[activeAiCategory ?? '_default'] ?? NP_PROMPTS._default;

    return (
      <div className={styles.aiPanel}>
        {!isSubscribed && (
          <div className={styles.notice}>
            <span><Trans message="AI builder is included in paid plans." /></span>
            <a href="/pricing" className={styles.noticeLink}><Trans message="See plans →" /></a>
          </div>
        )}

        {/* Input box */}
        <div className={styles.aiInputBox}>
          <textarea
            ref={aiTextareaRef}
            className={styles.aiInputTextarea}
            rows={3}
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!aiSubmitDisabled) handleGenerateWithAi();
              }
            }}
            placeholder={trans(message("Describe your website and we'll build it for you…"))}
            maxLength={AI_PROMPT_MAX_LENGTH}
            disabled={aiBuilder.isPending}
          />
          <div className={styles.aiInputFooter}>
            <span className={styles.aiInputCharCount}>
              {aiPrompt.trim().length}
              <span className={styles.aiInputCharMax}>/{AI_PROMPT_MAX_LENGTH}</span>
            </span>
            <div className={styles.aiInputRight}>
              {!isSubscribed && (
                <span className={styles.aiInputPlanTag}><Trans message="Pro" /></span>
              )}
              <button
                type="button"
                className={styles.aiInputSubmit}
                disabled={aiSubmitDisabled}
                onClick={handleGenerateWithAi}
                aria-label={trans(message('Generate'))}
              >
                {aiBuilder.isPending ? (
                  <span className={styles.aiInputSpinner} />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {aiError && <div className={styles.error}>{aiError}</div>}

        {/* Category chips */}
        <div className={styles.aiChipsRow}>
          <button
            type="button"
            className={styles.aiChipsNav}
            aria-label="Scroll left"
            onClick={() => {
              const el = document.querySelector(`.${styles.aiChipsList}`) as HTMLElement;
              el?.scrollBy({left: -180, behavior: 'smooth'});
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <div className={styles.aiChipsList}>
            {NP_CATEGORIES.map(label => (
              <button
                key={label}
                type="button"
                className={clsx(styles.aiChip, activeAiCategory === label && styles.aiChipActive)}
                onClick={() => setActiveAiCategory(prev => prev === label ? null : label)}
              >
                {activeAiCategory === label && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" style={{color:'var(--np-accent)'}}>
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                )}
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.aiChipsNav}
            aria-label="Scroll right"
            onClick={() => {
              const el = document.querySelector(`.${styles.aiChipsList}`) as HTMLElement;
              el?.scrollBy({left: 180, behavior: 'smooth'});
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>

        {/* Example prompts */}
        <div className={styles.aiPromptsRow}>
          <span className={styles.aiPromptsLabel}>
            {activeAiCategory
              ? `${activeAiCategory} ideas`
              : trans(message('Try an example'))}
          </span>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeAiCategory ?? '_default'}
              className={styles.aiPromptsPills}
              initial={{opacity: 0, y: 4}}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: -4}}
              transition={{duration: 0.14, ease: 'easeOut'}}
            >
              {prompts.map(p => (
                <button
                  key={p}
                  type="button"
                  className={styles.aiPromptPill}
                  onClick={() => { setAiPrompt(p); aiTextareaRef.current?.focus(); }}
                >
                  {p}
                </button>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  };

  const renderGeneratingOverlay = () => (
    <AnimatePresence>
      {aiBuilder.isPending && (
        <motion.div
          className={styles.generatingOverlay}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.22, ease: 'easeOut'}}
        >
          <motion.div
            className={styles.generatingBackdrop}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.28, ease: 'easeOut'}}
          >
            {/* Spinner */}
            <div className={styles.generatingThing} role="status" aria-live="polite" aria-label="Generating">
              <span className={styles.generatingRingOuter} />
              <span className={styles.generatingRingInner} />
              <span className={styles.generatingCore} />
            </div>

            {/* Step list */}
            <div className={styles.genSteps}>
              {GEN_STEPS.map((label, i) => {
                const done   = i < genStep;
                const active = i === genStep;
                return (
                  <motion.div
                    key={label}
                    className={styles.genStep}
                    initial={{opacity: 0, x: -6}}
                    animate={{opacity: 1, x: 0}}
                    transition={{delay: i * 0.06, duration: 0.2, ease: 'easeOut'}}
                  >
                    {/* dot */}
                    <span className={clsx(
                      styles.genStepDot,
                      done   && styles['genStepDot--done'],
                      active && styles['genStepDot--active'],
                    )}>
                      {done && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                      )}
                    </span>

                    {/* label */}
                    <span className={clsx(
                      styles.genStepLabel,
                      done   && styles['genStepLabel--done'],
                      active && styles['genStepLabel--active'],
                    )}>
                      {label}
                    </span>

                    {/* per-step spinner */}
                    {active && <span className={styles.genStepSpinner} />}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Desktop navbar — hidden on mobile */}
      <span className="db-desktop-only">
        <DashboardNavbar />
      </span>
      <div className={styles.page}>
        <StaticPageTitle>
          <Trans message="New project" />
        </StaticPageTitle>

        {/* ── Mobile top bar ─────────────────────────── */}
        {(() => {
          const firstName = (user as any)?.first_name || (user as any)?.display_name?.split(' ')[0] || null;
          const initials = ((user as any)?.first_name?.[0] ?? '') + ((user as any)?.last_name?.[0] ?? (user as any)?.display_name?.[1] ?? '');
          const displayInitials = (initials || (user as any)?.display_name?.[0] || 'U').toUpperCase().slice(0, 2);
          const wsName = (user as any)?.display_name || (user as any)?.first_name
            ? `${(user as any)?.display_name ?? (user as any)?.first_name}'s workspace`
            : 'My workspace';
          return (
            <div className={styles.mobileCrTop}>
              <div className={styles.mobileCrWsBadge} role="button" tabIndex={0}>
                <span className={styles.mobileCrWsAvatar}>{displayInitials}</span>
                <span className={styles.mobileCrWsName}>{wsName}</span>
              </div>
              <button type="button" className={styles.mobileCrMenuBtn} aria-label="Menu">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <circle cx="12" cy="5" r="1.8"/>
                  <circle cx="12" cy="12" r="1.8"/>
                  <circle cx="12" cy="19" r="1.8"/>
                </svg>
              </button>
            </div>
          );
        })()}

        {/* ── Mobile greeting ────────────────────────── */}
        <h1 className={styles.mobileGreeting}>
          {(() => {
            const firstName = (user as any)?.first_name || (user as any)?.display_name?.split(' ')[0] || null;
            return firstName
              ? `Hi ${firstName}, what do you want to make?`
              : 'What do you want to make?';
          })()}
        </h1>

        <div className={styles.shell}>
          {/* ── Mode card selector ───────────────────── */}
          {enabledModes.length === 0 ? (
            <div className={styles.notice}>
              <span><Trans message="No builder modes are currently enabled. Please contact your site administrator." /></span>
            </div>
          ) : (
          <div className={clsx(styles.modeCards, enabledModes.length === 1 && styles.modeCardsSingle, enabledModes.length === 2 && styles.modeCardsDouble)}>
            {/* AI Builder */}
            {aiModeEnabled && (
            <button
              type="button"
              className={clsx(styles.modeCard, mode === 'ai' && styles.modeCardActive)}
              onClick={() => handleModeSelect('ai')}
            >
              <div className={clsx(styles.modeCardIcon, styles['modeCardIcon--ai'])}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a2 2 0 012 2v1a2 2 0 002 2h1a2 2 0 010 4h-1a2 2 0 00-2 2v1a2 2 0 01-4 0v-1a2 2 0 00-2-2H7a2 2 0 010-4h1a2 2 0 002-2V4a2 2 0 012-2z"/>
                  <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none"/>
                </svg>
              </div>
              <div>
                <div className={styles.modeCardTitle}><Trans message="AI Builder" /></div>
                <div className={styles.modeCardDesc}><Trans message="Describe your site in plain language — AI generates the full layout, copy, and styles." /></div>
              </div>
              <span className={styles.modeCardTag}><Trans message="Fastest" /></span>
              {!isSubscribed && <span className={styles.modeCardPro}>Pro</span>}
              {mode === 'ai' && (
                <span className={styles.modeCardCheck}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                </span>
              )}
            </button>
            )}

            {/* Template */}
            {templateModeEnabled && (
            <button
              type="button"
              className={clsx(styles.modeCard, mode === 'template' && styles.modeCardActive)}
              onClick={() => handleModeSelect('template')}
            >
              <div className={clsx(styles.modeCardIcon, styles['modeCardIcon--template'])}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <rect x="3" y="3" width="8" height="8" rx="2"/>
                  <rect x="13" y="3" width="8" height="8" rx="2"/>
                  <rect x="3" y="13" width="8" height="8" rx="2"/>
                  <rect x="13" y="13" width="8" height="8" rx="2"/>
                </svg>
              </div>
              <div>
                <div className={styles.modeCardTitle}><Trans message="Templates" /></div>
                <div className={styles.modeCardDesc}><Trans message="Pick a professionally designed template and customise it to match your brand." /></div>
              </div>
              <span className={styles.modeCardTag}><Trans message="Great starting point" /></span>
              {mode === 'template' && (
                <span className={styles.modeCardCheck}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                </span>
              )}
            </button>
            )}

            {/* Drag & Drop */}
            {dragDropModeEnabled && (
            <button
              type="button"
              className={clsx(styles.modeCard, mode === 'blank' && styles.modeCardActive)}
              onClick={() => handleModeSelect('blank')}
            >
              <div className={clsx(styles.modeCardIcon, styles['modeCardIcon--dragdrop'])}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <path d="M5 9l4-4 4 4M5 15l4 4 4-4M15 9l4-4M15 15l4 4"/>
                  <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                </svg>
              </div>
              <div>
                <div className={styles.modeCardTitle}><Trans message="Drag & Drop" /></div>
                <div className={styles.modeCardDesc}><Trans message="Start with a blank canvas and build every section visually — no code needed." /></div>
              </div>
              <span className={styles.modeCardTag}><Trans message="Full control" /></span>
              {mode === 'blank' && (
                <span className={styles.modeCardCheck}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                </span>
              )}
            </button>
            )}
          </div>
          )}

          {error && mode !== 'ai' && (
            <div className={styles.notice}>
              <span>{error}</span>
              {!isSubscribed && (
                <a href="/pricing" className={styles.noticeLink}>
                  <Trans message="Upgrade →" />
                </a>
              )}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              className={styles.panel}
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: -8}}
              transition={{duration: 0.15, ease: 'easeOut'}}
            >
              {mode === 'ai' && renderAiPanel()}
              {mode === 'template' && renderTemplatePanel()}
              {mode === 'blank' && renderBlankPanel()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      {/* Mobile bottom nav */}
      <DashboardBottomNav activeTab="create" />
      {renderGeneratingOverlay()}

      {/* Admin debug panel */}
      {isAdmin && (
        <AiDebugPanel
          settings={settings}
          debugInfo={debugInfo}
          debugRequest={debugRequest}
          genLog={genLog}
          isPending={aiBuilder.isPending}
          onClearLog={() => setGenLog([])}
        />
      )}
    </>
  );
}

// ─── AI Debug Panel (admin-only) ────────────────────────────────────────────

interface LogEntry {ts: number; msg: string; type: 'info'|'ok'|'err';}

function AiDebugPanel({
  settings,
  debugInfo,
  debugRequest,
  genLog,
  isPending,
  onClearLog,
}: {
  settings: any;
  debugInfo: AiDebugInfo | null;
  debugRequest: Record<string,string> | null;
  genLog: LogEntry[];
  isPending: boolean;
  onClearLog: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [testStatus, setTestStatus] = useState<null | 'loading' | {ok: boolean; data: any}>(null);
  const logRef = useRef<HTMLDivElement>(null);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [genLog]);

  const runTest = async () => {
    setTestStatus('loading');
    try {
      const res = await apiClient.get('admin/ai/test-connection');
      setTestStatus({ok: res.data.status === 'ok', data: res.data});
    } catch (e: any) {
      setTestStatus({ok: false, data: {message: e.message}});
    }
  };

  // Read active provider from settings (client-side) or from debugInfo (after a call)
  const cfgProvider = debugInfo?.provider ?? settings?.server?.ai?.provider ?? '—';
  const cfgModel    = debugInfo?.model    ?? '—';
  const apiKeySet   = debugInfo?.api_key_set;
  const apiKeyHint  = debugInfo?.api_key_hint;

  const hasError   = debugInfo?.status === 'error';
  const hasSuccess = debugInfo?.status === 'success';

  const badge = (color: string): CSSProperties => ({
    padding: '1px 7px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    background: color,
    color: '#fff',
  });

  const s: Record<string, CSSProperties> = {
    panel: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      background: '#0d0d0d',
      borderTop: '1px solid rgba(255,255,255,.08)',
      fontFamily: "'SFMono-Regular', Menlo, Monaco, 'Courier New', monospace",
      fontSize: '12px',
      color: '#d4d4d4',
      maxHeight: open ? '420px' : '38px',
      overflow: 'hidden',
      transition: 'max-height .2s ease',
    },
    topBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '0 14px',
      height: '38px',
      borderBottom: open ? '1px solid rgba(255,255,255,.06)' : 'none',
      background: '#111',
      cursor: 'pointer',
      userSelect: 'none' as const,
      flexShrink: 0,
    },
    body: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '1px',
      height: '382px',
      overflow: 'hidden',
      background: 'rgba(255,255,255,.05)',
    },
    col: {
      background: '#0d0d0d',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    colHead: {
      padding: '6px 12px',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: '#666',
      textTransform: 'uppercase' as const,
      borderBottom: '1px solid rgba(255,255,255,.05)',
      flexShrink: 0,
    },
    colBody: {
      flex: 1,
      overflow: 'auto',
      padding: '10px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    row: {
      display: 'flex',
      gap: '8px',
      lineHeight: 1.5,
    },
    key: {
      color: '#6b7280',
      flexShrink: 0,
      minWidth: '90px',
    },
    val: {color: '#e5e7eb'},
    errVal: {color: '#f87171'},
    okVal: {color: '#4ade80'},
    traceItem: {
      padding: '4px 0',
      borderBottom: '1px solid rgba(255,255,255,.04)',
    },
    traceFile: {color: '#818cf8'},
    traceCall: {color: '#94a3b8', fontSize: '11px'},
    testBtn: {
      marginTop: 'auto',
      padding: '7px 14px',
      background: 'rgba(99,102,241,.18)',
      color: '#818cf8',
      border: '1px solid rgba(99,102,241,.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '11px',
      fontFamily: 'inherit',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
  };

  return (
    <div style={s.panel}>
      {/* Top bar */}
      <div style={s.topBar} onClick={() => setOpen(p => !p)}>
        <span style={{...badge('#1d4ed8'), fontSize: '9px'}}>ADMIN</span>
        <span style={{color: '#9ca3af', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em'}}>
          AI DEBUG
        </span>
        {isPending && (
          <span style={{...badge('#d97706')}}>RUNNING</span>
        )}
        {hasError && <span style={{...badge('#dc2626')}}>ERROR</span>}
        {hasSuccess && <span style={{...badge('#16a34a')}}>OK</span>}
        <span style={{color: '#555', fontSize: '11px'}}>
          {cfgProvider !== '—' && `${cfgProvider} · ${cfgModel}`}
        </span>
        <span style={{marginLeft: 'auto', color: '#555', fontSize: '18px', lineHeight: 1}}>
          {open ? '▾' : '▴'}
        </span>
      </div>

      {open && (
        <div style={s.body}>
          {/* COL 1: Config + Test */}
          <div style={s.col}>
            <div style={s.colHead}>Config</div>
            <div style={{...s.colBody}}>
              <div style={s.row}><span style={s.key}>provider</span><span style={s.val}>{cfgProvider}</span></div>
              <div style={s.row}><span style={s.key}>model</span><span style={s.val}>{cfgModel}</span></div>
              {apiKeySet !== undefined && (
                <div style={s.row}>
                  <span style={s.key}>api key</span>
                  <span style={apiKeySet ? s.okVal : s.errVal}>
                    {apiKeySet ? `✓ set  (${apiKeyHint ?? ''})` : '✗ not set'}
                  </span>
                </div>
              )}
              {debugInfo?.elapsed_ms !== undefined && (
                <div style={s.row}><span style={s.key}>elapsed</span><span style={s.val}>{debugInfo.elapsed_ms}ms</span></div>
              )}
              {debugInfo?.tokens !== undefined && (
                <div style={s.row}><span style={s.key}>tokens</span><span style={s.val}>{debugInfo.tokens}</span></div>
              )}

              <div style={{marginTop: '12px', borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: '10px'}}>
                <button style={s.testBtn} onClick={runTest} disabled={testStatus === 'loading'}>
                  {testStatus === 'loading' ? (
                    <span style={{display: 'inline-block', width: '10px', height: '10px', border: '2px solid #818cf8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite'}} />
                  ) : (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  )}
                  Test Connection
                </button>
                {testStatus && testStatus !== 'loading' && (
                  <div style={{marginTop: '8px', padding: '8px', background: testStatus.ok ? 'rgba(22,163,74,.1)' : 'rgba(220,38,38,.1)', borderRadius: '4px', border: `1px solid ${testStatus.ok ? 'rgba(22,163,74,.3)' : 'rgba(220,38,38,.3)'}`}}>
                    <div style={{color: testStatus.ok ? '#4ade80' : '#f87171', fontWeight: 700, marginBottom: '4px'}}>
                      {testStatus.ok ? '✓ Connection OK' : '✗ Connection Failed'}
                    </div>
                    {testStatus.ok ? (
                      <>
                        <div style={s.row}><span style={s.key}>provider</span><span style={s.val}>{testStatus.data.config?.provider}</span></div>
                        <div style={s.row}><span style={s.key}>model</span><span style={s.val}>{testStatus.data.config?.model}</span></div>
                        <div style={s.row}><span style={s.key}>response</span><span style={s.okVal}>{testStatus.data.response}</span></div>
                        <div style={s.row}><span style={s.key}>elapsed</span><span style={s.val}>{testStatus.data.elapsed_ms}ms</span></div>
                      </>
                    ) : (
                      <div style={{...s.errVal, wordBreak: 'break-all' as const}}>{testStatus.data.message}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* COL 2: Error / Request */}
          <div style={s.col}>
            <div style={s.colHead}>{hasError ? 'Error Details' : 'Last Request'}</div>
            <div style={s.colBody}>
              {hasError && debugInfo ? (
                <>
                  <div style={{...s.errVal, fontWeight: 700, marginBottom: '4px', wordBreak: 'break-all' as const}}>{debugInfo.message}</div>
                  <div style={s.row}><span style={s.key}>exception</span><span style={{...s.val, wordBreak: 'break-all' as const}}>{debugInfo.exception}</span></div>
                  <div style={s.row}><span style={s.key}>file</span><span style={{...s.traceFile, wordBreak: 'break-all' as const}}>{debugInfo.file}:{debugInfo.line}</span></div>
                  {debugInfo.api_key_set !== undefined && (
                    <div style={s.row}>
                      <span style={s.key}>api_key</span>
                      <span style={debugInfo.api_key_set ? s.okVal : s.errVal}>
                        {debugInfo.api_key_set ? `set (${debugInfo.api_key_hint})` : 'MISSING — set in Admin → Settings → AI'}
                      </span>
                    </div>
                  )}
                  {debugInfo.trace && debugInfo.trace.length > 0 && (
                    <>
                      <div style={{color: '#555', fontSize: '10px', marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.05em'}}>Stack</div>
                      {debugInfo.trace.map((t, i) => (
                        <div key={i} style={s.traceItem}>
                          <div style={s.traceCall}>{t.call}</div>
                          <div style={s.traceFile}>{t.file}:{t.line}</div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : debugRequest ? (
                <>
                  <div style={{color: '#818cf8', fontWeight: 600, marginBottom: '6px'}}>POST /api/projects/ai</div>
                  {Object.entries(debugRequest).map(([k, v]) => (
                    <div key={k} style={s.row}>
                      <span style={s.key}>{k}</span>
                      <span style={{...s.val, wordBreak: 'break-all' as const}}>{String(v).length > 80 ? String(v).slice(0, 80) + '…' : v}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{color: '#4b5563', marginTop: '20px', textAlign: 'center' as const}}>
                  Run an AI generation to see details here.
                </div>
              )}
            </div>
          </div>

          {/* COL 3: Live Log */}
          <div style={s.col}>
            <div style={s.colHead}>
              Live Log
              {genLog.length > 0 && (
                <span style={{float: 'right', color: '#444', cursor: 'pointer', fontWeight: 400}} onClick={onClearLog}>
                  clear
                </span>
              )}
            </div>
            <div ref={logRef} style={{...s.colBody, gap: '3px'}}>
              {genLog.length === 0 ? (
                <div style={{color: '#4b5563', marginTop: '20px', textAlign: 'center' as const}}>Waiting for activity…</div>
              ) : (
                genLog.map((entry, i) => {
                  const t = new Date(entry.ts);
                  const ts = `${String(t.getHours()).padStart(2,'0')}:${String(t.getMinutes()).padStart(2,'0')}:${String(t.getSeconds()).padStart(2,'0')}.${String(t.getMilliseconds()).padStart(3,'0')}`;
                  return (
                    <div key={i} style={{display: 'flex', gap: '8px', lineHeight: '1.6', wordBreak: 'break-all' as const}}>
                      <span style={{color: '#374151', flexShrink: 0}}>{ts}</span>
                      <span style={entry.type === 'err' ? s.errVal : entry.type === 'ok' ? s.okVal : {color: '#9ca3af'}}>
                        {entry.msg}
                      </span>
                    </div>
                  );
                })
              )}
              {isPending && (
                <div style={{display: 'flex', gap: '6px', alignItems: 'center', color: '#d97706', marginTop: '2px'}}>
                  <span style={{display: 'inline-block', width: '8px', height: '8px', border: '1.5px solid #d97706', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0}} />
                  Awaiting AI response…
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
